import type { TimelineEvent } from "../api/types";
import { readContextRefs } from "./session";

/** Constantes + helpers de la vue Timeline (design Chronos Enrichissement) :
 * rail 06:00→22:00, 0.9px/min, créneaux au pas de 15 min. */
export const DAY_START_MIN = 6 * 60;
export const DAY_END_MIN = 22 * 60;
export const PX_PER_MIN = 0.9;
export const STEP_MIN = 15;
/** Hauteur peinte minimale d'un bloc (px) — un bloc plus court est étiré. */
export const MIN_BLOCK_PX = 14;
/** Hauteur peinte d'un marqueur (px) — un marqueur est un instant, pas une durée. */
export const MARKER_PX = 20;

export function minToLabel(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function clampMin(min: number): number {
  return Math.min(DAY_END_MIN, Math.max(DAY_START_MIN, min));
}

/** Horodatage ISO local d'un jour + minute-du-jour (créneau timeline). */
export function slotIso(day: Date, minOfDayValue: number): string {
  const d = new Date(day);
  d.setHours(Math.floor(minOfDayValue / 60), minOfDayValue % 60, 0, 0);
  return d.toISOString();
}

function minOfDay(iso: string): number {
  const d = new Date(iso);
  return d.getHours() * 60 + d.getMinutes();
}

export interface ActivityBlock {
  startMin: number;
  endMin: number;
  code: string;
  live: boolean;
  projects: { id: string; label?: string }[];
  tasks: { id: string; label?: string }[];
  lteTasks: { id: string; label?: string }[];
}

export interface TimelineMarker {
  min: number;
  type: string;
  label: string;
}

/** Paires activity_start/activity_end du jour → blocs. Un start sans end = en cours.
 * Plusieurs blocs peuvent être ouverts en parallèle : un end ferme le dernier
 * bloc ouvert de même code (LIFO), à défaut le dernier ouvert tout court. */
export function buildBlocks(dayEvents: TimelineEvent[], isToday: boolean) {
  const sorted = [...dayEvents].sort(
    (a, b) =>
      new Date(a.occurred_at).getTime() - new Date(b.occurred_at).getTime(),
  );
  const blocks: ActivityBlock[] = [];
  const markers: TimelineMarker[] = [];
  const open: ActivityBlock[] = [];

  const close = (b: ActivityBlock, min: number) => {
    b.endMin = Math.min(DAY_END_MIN, Math.max(min, b.startMin + 5));
    blocks.push(b);
    open.splice(open.indexOf(b), 1);
  };

  for (const ev of sorted) {
    const min = clampMin(minOfDay(ev.occurred_at));
    const refCode = (ev.context_refs as { activity_code?: unknown } | null)
      ?.activity_code;
    const code =
      ev.activity_code ?? (typeof refCode === "string" ? refCode : "");
    switch (ev.event_type) {
      case "activity_start": {
        const { projects, tasks, lteTasks } = readContextRefs(ev.context_refs);
        open.push({
          startMin: min,
          endMin: min,
          code,
          live: false,
          projects,
          tasks,
          lteTasks,
        });
        break;
      }
      case "activity_end": {
        const match =
          (code && [...open].reverse().find((b) => b.code === code)) ||
          open[open.length - 1];
        if (match) close(match, min);
        break;
      }
      case "attendance_in":
        markers.push({ min, type: "in", label: "Entrée" });
        break;
      case "attendance_out":
        for (const b of [...open]) close(b, min);
        markers.push({ min, type: "out", label: "Sortie" });
        break;
      case "break_start":
        markers.push({ min, type: "break", label: "Pause" });
        break;
      case "break_end":
        markers.push({ min, type: "break", label: "Reprise" });
        break;
    }
  }
  if (open.length) {
    const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
    open.forEach((b, i) => {
      b.endMin = isToday
        ? clampMin(Math.max(nowMin, b.startMin + 5))
        : DAY_END_MIN;
      b.live = isToday && i === open.length - 1;
      blocks.push(b);
    });
  }
  blocks.sort((a, b) => a.startMin - b.startMin);
  return { blocks, markers };
}

/** Tout ce qui occupe une plage verticale du rail : bloc d'activité, marqueur… */
export interface Laneable {
  startMin: number;
  endMin: number;
  /** Hauteur peinte minimale (px) ; défaut MIN_BLOCK_PX. */
  minPx?: number;
}

export type LaidOut<T> = T & { lane: number; lanes: number };

/** Répartit en colonnes (type Google Calendar) tout ce qui se chevauche sur le
 * rail — blocs et marqueurs confondus, pour qu'aucun élément n'en masque un autre.
 * Cluster = suite d'items reliés par chevauchement *visuel*, pas temporel : un item
 * plus court que sa hauteur minimale est peint au-delà de son empreinte temporelle
 * (un marqueur est même un instant : startMin === endMin). On raisonne donc sur la
 * fin visuelle (vEnd) — sans quoi deux activités de 5 min consécutives, ou deux
 * marqueurs proches, se superposeraient en pleine largeur. Dans un cluster, chaque
 * item prend la première lane libre. */
export function layoutLanes<T extends Laneable>(items: T[]): LaidOut<T>[] {
  const vEnd = (b: T) =>
    Math.max(b.endMin, b.startMin + (b.minPx ?? MIN_BLOCK_PX) / PX_PER_MIN);
  const sorted = [...items].sort(
    (a, b) =>
      a.startMin - b.startMin ||
      b.endMin - b.startMin - (a.endMin - a.startMin),
  );
  const out: LaidOut<T>[] = [];
  let cluster: LaidOut<T>[] = [];
  let laneEnds: number[] = [];
  let clusterEnd = -Infinity;

  const flush = () => {
    for (const b of cluster) b.lanes = laneEnds.length;
    out.push(...cluster);
    cluster = [];
    laneEnds = [];
  };

  for (const b of sorted) {
    if (b.startMin >= clusterEnd) flush();
    let lane = laneEnds.findIndex((end) => end <= b.startMin);
    if (lane === -1) lane = laneEnds.length;
    laneEnds[lane] = vEnd(b);
    cluster.push({ ...b, lane, lanes: 1 });
    clusterEnd = Math.max(clusterEnd, vEnd(b));
  }
  flush();
  return out;
}

/** Borne haute (minute du jour) de fin d'un créneau saisissable sur `day`.
 * Un activity_end postérieur au start de l'activité en cours effacerait
 * l'état live dérivé (deriveSessionState) : on interdit donc tout créneau
 * dans le futur ou chevauchant une activité en cours démarrée le jour même.
 * null = pas de borne (jour passé sans activité live ce jour-là) ;
 * DAY_START_MIN = jour futur, entièrement bloqué. */
export function slotMaxEndMin(
  day: Date,
  nowMs: number,
  activeStartMs: number | null,
): number | null {
  const dayStart = new Date(day);
  dayStart.setHours(0, 0, 0, 0);
  const nextDay = dayStart.getTime() + 24 * 60 * 60 * 1000;

  if (nowMs < dayStart.getTime()) return DAY_START_MIN; // jour futur

  let max: number | null = null;
  if (nowMs < nextDay) {
    const n = new Date(nowMs);
    max = n.getHours() * 60 + n.getMinutes(); // aujourd'hui : pas de futur
  }
  // Anti-chevauchement : seule une activité live démarrée le jour édité borne
  // les créneaux (un créneau finissant après effacerait son état live). Une
  // session live d'un jour antérieur — le plus souvent une sortie non badgée
  // la veille — est ignorée ; sinon toute la journée resterait bloquée.
  if (
    activeStartMs != null &&
    activeStartMs >= dayStart.getTime() &&
    activeStartMs < nextDay
  ) {
    const a = new Date(activeStartMs);
    max = Math.min(max ?? Infinity, a.getHours() * 60 + a.getMinutes());
  }
  return max;
}

/** Normalise un drag/tap (deux minutes-du-jour) en créneau [start, end).
 * Tap (Δ < STEP_MIN) → 1 h ; si le haut plafonne à DAY_END_MIN, on étend vers
 * le bas pour qu'un tap à 22:00 reste saisissable. null = aucun créneau valide. */
export function resolveSlotRange(
  a: number,
  b: number,
): { start: number; end: number } | null {
  let start = Math.min(a, b);
  let end = Math.max(a, b);
  if (end - start < STEP_MIN) {
    end = clampMin(start + 60);
    if (end - start < STEP_MIN) start = clampMin(end - 60);
  }
  return end > start ? { start, end } : null;
}
